import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box, AppBar, Toolbar, Container, Typography, Avatar, Button, Stack, Chip,
  Card, CardContent, CardMedia, CardActions, Grid, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Radio, RadioGroup, Checkbox,
  FormControlLabel, Divider, TextField, IconButton, Tooltip,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { QRCodeSVG } from "qrcode.react";
import Swal from "sweetalert2";
import { AuthTabs } from "../Inscricao/CandidatoAuth";
import { instituicaoDe, instituicaoDoHost } from "../../config/instituicoes";
import {
  getEventosPublicos, getEventoPublico, eventoBannerUrl, inscreverEvento, minhasInscricoesEvento, baixarCertificado,
  historicoEventos, baixarCertificadoHistorico,
  getCobranca, candidatoLogado, getCandidato, logoutCandidato, getMatriculasEvento, regerarPix,
} from "../../services/candidatoService";

const fmtData = (d) => (d ? new Date(d).toLocaleDateString("pt-BR") : null);
const fmtDT = (d) => (d ? new Date(d).toLocaleString("pt-BR") : "—");
const moeda = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function PixBox({ cob }) {
  // Pago (ou sem valor/cancelado): o PIX não deve ser exibido em hipótese alguma.
  if (!cob || cob.Pago || cob.Status === "C" || cob.Status === "X" || !(cob.ValorTotal > 0)) return null;
  // Vencido: o código não é mais pagável — só resta gerar outro (botão fica fora, no chamador).
  if (cob.PixExpirado) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        O código PIX venceu{cob.PixExpiraEm ? ` em ${fmtDT(cob.PixExpiraEm)}` : ""} e não pode mais ser pago.
        Gere um novo código abaixo para concluir o pagamento.
      </Alert>
    );
  }
  const copiar = () => { navigator.clipboard?.writeText(cob.PixCopiaCola || ""); Swal.fire({ toast: true, position: "top-end", icon: "success", title: "PIX copiado", showConfirmButton: false, timer: 1500 }); };
  return (
    <Box sx={{ mt: 1 }}>
      {cob.PixExpiraEm && cob.PixCopiaCola && (
        <Alert severity="info" sx={{ mb: 1, py: 0.2 }}>Pague até <b>{fmtDT(cob.PixExpiraEm)}</b> — depois disso é preciso gerar um novo código.</Alert>
      )}
      {cob.PixCopiaCola && (
        <Box sx={{ textAlign: "center", mb: 1.5 }}>
          {/* QR gerado no navegador a partir do copia-e-cola (não depende do backend). */}
          <Box sx={{ display: "inline-block", p: 1.5, bgcolor: "#fff", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <QRCodeSVG value={cob.PixCopiaCola} size={190} level="M" />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Abra o app do banco, escolha PIX → Ler QR Code
          </Typography>
        </Box>
      )}
      {cob.PixCopiaCola ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField fullWidth size="small" label="PIX copia e cola" value={cob.PixCopiaCola} InputProps={{ readOnly: true }} />
          <Tooltip title="Copiar"><IconButton onClick={copiar}><ContentCopyIcon /></IconButton></Tooltip>
        </Stack>
      ) : cob.PixErro ? (
        <Alert severity="error">
          <b>Não foi possível gerar o PIX.</b>
          <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontSize: 11, m: 0, mt: 0.5 }}>{cob.PixErro}</Box>
        </Alert>
      ) : (
        <Alert severity="warning">Não foi possível gerar o código PIX automaticamente. Procure a instituição.</Alert>
      )}
    </Box>
  );
}

// Itens (alunos) de uma cobrança, com o certificado de cada participante.
function ItensCobranca({ itens, onCertificado }) {
  if (!itens?.length) return null;
  return (
    <Box sx={{ mt: 0.5 }}>
      {itens.map((it) => (
        <Box key={it.Id} sx={{ mb: 0.3 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {it.AlunoNome || "Inscrição"}{it.Turma ? ` — Turma ${it.Turma}` : ""} • {it.ValorDevido > 0 ? moeda(it.ValorDevido) : "isento"}
          </Typography>
          {it.Certificado && (it.Certificado.Liberado || it.Certificado.Motivo !== "Este evento não emite certificado.") && (
            it.Certificado.Liberado ? (
              <Button size="small" startIcon={<WorkspacePremiumIcon />} onClick={() => onCertificado(it.Id)} sx={{ mt: 0.2 }}>
                Baixar certificado{it.Certificado.CargaHoraria ? ` (${it.Certificado.CargaHoraria}h)` : ""}
              </Button>
            ) : (
              <Typography variant="caption" color="text.disabled" display="block">
                Certificado: {it.Certificado.Motivo}
              </Typography>
            )
          )}
        </Box>
      ))}
    </Box>
  );
}

// Oficinas escolhidas na inscrição.
function OficinasCob({ oficinas }) {
  if (!oficinas?.length) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block"><b>Oficinas</b></Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.3 }}>
        {oficinas.map((o, i) => (
          <Chip key={i} size="small" variant="outlined" label={o.Grupo ? `${o.Grupo}: ${o.Opcao}` : o.Opcao} />
        ))}
      </Stack>
    </Box>
  );
}

// Frequência nos encontros do evento.
function FrequenciaCob({ encontros }) {
  if (!encontros?.length) return null;
  const presentes = encontros.filter((e) => e.Presente).length;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        <b>Frequência</b> — {presentes} de {encontros.length} encontro(s)
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.3 }}>
        {encontros.map((en) => (
          <Tooltip key={en.Id} title={en.Presente && en.DtRegistro ? `Registrada em ${fmtDT(en.DtRegistro)}` : "Sem registro"}>
            <Chip size="small" color={en.Presente ? "success" : "default"} variant={en.Presente ? "filled" : "outlined"}
              label={`${en.Nome}${en.Data ? ` (${fmtData(en.Data)})` : ""}${en.Presente ? " ✓" : ""}`} />
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
}

export default function EventosPublico() {
  const { id: idParam } = useParams();
  const [eventos, setEventos] = useState(null);
  const [autoAberto, setAutoAberto] = useState(false);
  const [logado, setLogado] = useState(candidatoLogado());
  const [sel, setSel] = useState(null);
  const [valorSel, setValorSel] = useState("");
  const [matriculas, setMatriculas] = useState(false); // false=n/a, null=carregando, []=nenhuma, [...]=lista
  const [matSels, setMatSels] = useState([]);          // matrículas escolhidas (array)
  const [opcSel, setOpcSel] = useState({});            // { idGrupo: idOpcao } — oficinas escolhidas
  const [resultado, setResultado] = useState(null);    // cobrança criada (com PIX)
  const [enviando, setEnviando] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [minhasOpen, setMinhasOpen] = useState(false);
  const [minhas, setMinhas] = useState([]);
  const [historico, setHistorico] = useState(null);   // null = carregando
  const [semLogo, setSemLogo] = useState(false);
  const cand = getCandidato();

  // Marca exibida: a do evento quando o link aponta para um (/eventos/:id);
  // senão, a do domínio em que o portal foi aberto.
  const idMarcaHost = instituicaoDoHost().id;
  const eventoDoLink = idParam && eventos ? eventos.find((e) => String(e.Id) === String(idParam)) : null;
  const marca = eventoDoLink ? instituicaoDe(eventoDoLink.Instituicao) : instituicaoDe(idMarcaHost);
  useEffect(() => { setSemLogo(false); }, [marca.id]);
  useEffect(() => { document.title = `Eventos — ${marca.nome}`; }, [marca.nome]);

  // Lista só os eventos deste portal; um link direto para evento de outra
  // instituição continua abrindo normalmente (buscado à parte).
  const carregar = useCallback(async () => {
    try {
      let lista = (await getEventosPublicos(idMarcaHost)).data;
      if (idParam && !lista.some((e) => String(e.Id) === String(idParam))) {
        try { lista = [(await getEventoPublico(idParam)).data, ...lista]; }
        catch { /* evento inexistente ou despublicado */ }
      }
      setEventos(lista);
    } catch { setEventos([]); }
  }, [idMarcaHost, idParam]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirInscricao = (ev) => {
    setSel(ev); setResultado(null); setMatSels([]); setMatriculas(false); setOpcSel({});
    // Já deixa a taxa (categoria) selecionada por padrão.
    setValorSel(ev.Pago && ev.Valores?.length ? String(ev.Valores[0].Id) : "");
    if (!candidatoLogado()) setAuthOpen(true);
  };

  // Busca as matrículas do CPF ao abrir eventos pagos/por-filho.
  useEffect(() => {
    if (!sel || !(sel.Pago || sel.CobrarPorFilho) || !logado) { setMatriculas(false); return; }
    let ativo = true;
    setMatriculas(null);
    getMatriculasEvento()
      .then((r) => {
        if (!ativo) return;
        const lista = r.data.habilitado ? r.data.matriculas : false;
        setMatriculas(lista);
        // Já deixa os alunos marcados por padrão (o responsável pode desmarcar).
        if (Array.isArray(lista)) setMatSels(lista.map((m) => m.Matricula));
      })
      .catch(() => { if (ativo) setMatriculas(false); });
    return () => { ativo = false; };
  }, [sel, logado]);

  useEffect(() => {
    if (autoAberto || !idParam || !eventos) return;
    const ev = eventos.find((e) => String(e.Id) === String(idParam));
    if (ev && ev.Aberto) abrirInscricao(ev);
    if (ev) setAutoAberto(true);
  }, [idParam, eventos, autoAberto]);

  const toggleMat = (matricula) =>
    setMatSels((arr) => (arr.includes(matricula) ? arr.filter((x) => x !== matricula) : [...arr, matricula]));

  // Valor da inscrição (categoria escolhida).
  const valorBase = () => {
    if (!sel?.Pago || !valorSel) return 0;
    const v = (sel.Valores || []).find((x) => String(x.Id) === String(valorSel));
    return Number(v?.Valor || 0);
  };
  // Adicional dos filhos elegíveis pela idade.
  const adicionalFilhos = () => {
    if (!sel?.CobrarPorFilho || !Array.isArray(matriculas)) return 0;
    return matriculas
      .filter((m) => matSels.includes(m.Matricula) && m.Idade != null && m.Idade >= sel.IdadeMinimaCobranca)
      .reduce((s) => s + Number(sel.ValorFilho || 0), 0);
  };
  const totalPrevisto = () => valorBase() + adicionalFilhos();

  // Só entram na tela os grupos que têm alguma opção para escolher.
  const gruposComOpcoes = () => (sel?.Grupos || []).filter((g) => (g.Opcoes || []).length > 0);

  const confirmar = async () => {
    if (sel.Pago && !valorSel) { Swal.fire("Atenção", "Escolha o valor da inscrição.", "warning"); return; }
    // Oficinas obrigatórias precisam de escolha (grupo sem opção é ignorado).
    const grupos = gruposComOpcoes();
    const faltando = grupos.find((g) => g.Obrigatorio && !opcSel[g.Id]);
    if (faltando) { Swal.fire("Atenção", `Escolha uma opção em "${faltando.Nome}".`, "warning"); return; }
    // Alunos são opcionais: sem nenhum marcado, confirma a inscrição só do responsável.
    if (sel.CobrarPorFilho && matSels.length === 0) {
      const c = await Swal.fire({
        title: "Inscrição sem aluno?",
        text: "Nenhum aluno foi selecionado. A inscrição será registrada somente em seu nome (responsável).",
        icon: "question", showCancelButton: true, confirmButtonText: "Sim, continuar", cancelButtonText: "Voltar",
      });
      if (!c.isConfirmed) return;
    }
    const opcoes = Object.values(opcSel).filter(Boolean).map(Number);
    setEnviando(true);
    try {
      const { data } = await inscreverEvento(sel.Id, valorSel ? Number(valorSel) : null, matSels, opcoes);
      setResultado(data.cobranca);
      carregarMinhas();
      if (data.cobranca.Pago || data.cobranca.ValorTotal === 0) {
        await Swal.fire("Inscrição confirmada!", data.mensagem, "success");
        setSel(null);
      }
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e?.response?.data || e.message, "error"); }
    finally { setEnviando(false); }
  };

  const carregarMinhas = useCallback(async () => {
    if (!candidatoLogado()) { setMinhas([]); return; }
    try { setMinhas((await minhasInscricoesEvento()).data); } catch { /* silencioso */ }
  }, []);
  useEffect(() => { carregarMinhas(); }, [carregarMinhas, logado]);

  const abrirMinhas = async () => {
    try {
      setMinhas((await minhasInscricoesEvento()).data);
      setMinhasOpen(true);
      setHistorico(null);
      // Participações no sistema anterior (mesmo CPF) — carregadas em paralelo.
      historicoEventos().then((r) => setHistorico(r.data)).catch(() => setHistorico([]));
    }
    catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const pegarCertificadoAntigo = async (codInscrito) => {
    Swal.fire({ title: "Gerando certificado…", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    try { await baixarCertificadoHistorico(codInscrito); Swal.close(); }
    catch (e) {
      const txt = e?.response?.data instanceof Blob
        ? JSON.parse(await e.response.data.text())?.mensagem : e?.response?.data?.mensagem;
      Swal.fire("Certificado indisponível", txt || e.message, "info");
    }
  };
  const atualizarMinha = async (id) => {
    try {
      const { data } = await getCobranca(id);
      setMinhas((arr) => arr.map((m) => (m.Id === id ? data : m)));
    } catch (e) { Swal.fire("Erro", e?.response?.data || e.message, "error"); }
  };

  // Gera o PIX novamente para uma inscrição ainda não paga.
  const gerarPixNovamente = async (id, atualizarResultado = false) => {
    Swal.fire({ title: "Gerando PIX…", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    try {
      const { data } = await regerarPix(id);
      setMinhas((arr) => arr.map((m) => (m.Id === id ? data.cobranca : m)));
      if (atualizarResultado) setResultado(data.cobranca);
      if (data.cobranca.PixCopiaCola) Swal.fire("Pronto", "PIX gerado com sucesso.", "success");
      else Swal.fire({ icon: "error", title: "Não foi possível gerar o PIX", html: `<pre style="text-align:left;white-space:pre-wrap;font-size:11px;max-height:280px;overflow:auto">${data.cobranca.PixErro || "-"}</pre>` });
    } catch (e) { Swal.fire("Erro", e?.response?.data?.mensagem || e.message, "error"); }
  };

  const pegarCertificado = async (idInscricao) => {
    Swal.fire({ title: "Gerando certificado…", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
    try { await baixarCertificado(idInscricao); Swal.close(); }
    catch (e) {
      // a API responde 400 com JSON dentro de um blob quando o certificado não está liberado
      const txt = e?.response?.data instanceof Blob
        ? JSON.parse(await e.response.data.text())?.mensagem : e?.response?.data?.mensagem;
      Swal.fire("Certificado indisponível", txt || e.message, "info");
    }
  };

  // Cobrança ativa do candidato para um evento (para mostrar "Ver inscrição").
  const minhaDoEvento = (idEvento) => minhas.find((m) => m.IdEvento === idEvento && m.Status !== "X");

  const sair = () => { logoutCandidato(); setLogado(false); };
  const aposLogin = () => { setLogado(true); setAuthOpen(false); };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" elevation={0} sx={{ background: marca.gradiente }}>
        <Toolbar>
          {marca.logo && !semLogo ? (
            <Box component="img" src={marca.logo} alt={marca.nome} onError={() => setSemLogo(true)}
              sx={{ height: 40, maxWidth: 140, objectFit: "contain", mr: 1.5 }} />
          ) : (
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.15)", mr: 1.5 }}><EventIcon /></Avatar>
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>Eventos</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>{marca.nome}</Typography>
          </Box>
          {logado ? (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" } }}>{cand?.Nome}</Typography>
              <Button color="inherit" variant="outlined" size="small" onClick={abrirMinhas} sx={{ borderColor: "rgba(255,255,255,0.5)" }}>Minhas inscrições</Button>
              <Button color="inherit" variant="outlined" size="small" onClick={sair} sx={{ borderColor: "rgba(255,255,255,0.5)" }}>Sair</Button>
            </Stack>
          ) : (
            <Button color="inherit" variant="outlined" size="small" onClick={() => setAuthOpen(true)} sx={{ borderColor: "rgba(255,255,255,0.5)" }}>Entrar</Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {eventos === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>
        ) : eventos.length === 0 ? (
          <Alert severity="info">Nenhum evento disponível no momento.</Alert>
        ) : (
          <Grid container spacing={2}>
            {eventos.map((ev) => (
              <Grid key={ev.Id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column", borderRadius: 3, transition: "transform 0.2s, box-shadow 0.2s", "&:hover": { transform: "translateY(-4px)", boxShadow: 6 } }}>
                  {ev.TemBanner && (
                    <Box sx={{ position: "relative", height: 280, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#121212" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: -20, left: -20, right: -20, bottom: -20,
                          backgroundImage: `url(${eventoBannerUrl(ev.Id)})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          filter: "blur(20px) brightness(0.4)",
                          zIndex: 0,
                        }}
                      />
                      <Box
                        component="img"
                        src={eventoBannerUrl(ev.Id)}
                        alt={ev.Nome}
                        sx={{
                          height: "100%",
                          width: "100%",
                          objectFit: "contain",
                          position: "relative",
                          zIndex: 1,
                          p: 1.5,
                          filter: "drop-shadow(0px 8px 12px rgba(0,0,0,0.4))",
                        }}
                      />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      {ev.Pago || ev.CobrarPorFilho ? <Chip size="small" color="primary" label={ev.CobrarPorFilho ? "Taxa por aluno" : "Pago"} /> : <Chip size="small" variant="outlined" label="Gratuito" />}
                      {!ev.Aberto && <Chip size="small" color="default" label="Inscrições encerradas" />}
                    </Stack>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{ev.Nome}</Typography>
                    {ev.DataInicio && <Typography variant="body2" color="text.secondary">{fmtData(ev.DataInicio)}{ev.Local ? ` • ${ev.Local}` : ""}</Typography>}
                    {ev.Descricao && <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">{ev.Descricao.slice(0, 140)}{ev.Descricao.length > 140 ? "…" : ""}</Typography>}
                    {ev.Pago && ev.Valores?.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1 }}>Inscrição a partir de <b>{moeda(Math.min(...ev.Valores.map((v) => v.Valor)))}</b></Typography>
                    )}
                    {ev.CobrarPorFilho && (
                      <Typography variant="body2" color="text.secondary">+ <b>{moeda(ev.ValorFilho)}</b> por aluno {ev.IdadeMinimaCobranca}+ anos</Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    {minhaDoEvento(ev.Id) ? (
                      <Button fullWidth variant="outlined" onClick={abrirMinhas}>
                        Ver minha inscrição{minhaDoEvento(ev.Id).Pago ? "" : minhaDoEvento(ev.Id).ValorTotal > 0 ? " (pagamento pendente)" : ""}
                      </Button>
                    ) : (
                      <Button fullWidth variant="contained" disabled={!ev.Aberto} onClick={() => abrirInscricao(ev)}>
                        {ev.Aberto ? "Inscrever-se" : "Encerrado"}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      {/* Dialog de inscrição */}
      <Dialog open={!!sel} onClose={() => setSel(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{sel?.Nome}</DialogTitle>
        <DialogContent dividers>
          {sel && (
            <>
              {sel.Descricao && <Typography variant="body2" sx={{ mb: 1 }}>{sel.Descricao}</Typography>}
              <Typography variant="body2" color="text.secondary">
                {sel.DataInicio ? `Início: ${fmtDT(sel.DataInicio)}` : ""}{sel.Local ? ` • ${sel.Local}` : ""}
              </Typography>
              <Divider sx={{ my: 1.5 }} />

              {!sel.Aberto && !resultado ? (
                <Alert severity="warning">As inscrições deste evento estão encerradas.</Alert>
              ) : !logado ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>Entre ou cadastre-se para concluir a inscrição.</Alert>
                  <AuthTabs onOk={aposLogin} embutido />
                </>
              ) : resultado ? (
                resultado.Pago || resultado.ValorTotal === 0 ? (
                  <Alert severity="success">Inscrição confirmada!</Alert>
                ) : (
                  <>
                    {!resultado.PixErro && <Alert severity="success" sx={{ mb: 1 }}>Inscrição registrada! Pague via PIX para confirmar.</Alert>}
                    <ItensCobranca itens={resultado.Itens} />
                    <Typography variant="body2" sx={{ mt: 0.5 }}><b>Total:</b> {moeda(resultado.ValorTotal)}</Typography>
                    <PixBox cob={resultado} />
                    {!resultado.PixCopiaCola && (
                      <Button sx={{ mt: 1 }} variant="contained" startIcon={<RefreshIcon />} onClick={() => gerarPixNovamente(resultado.Id, true)}>
                        Gerar PIX novamente
                      </Button>
                    )}
                  </>
                )
              ) : (
                <>
                  {/* 1) Valor da inscrição (categoria) */}
                  {sel.Pago && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Valor da inscrição:</Typography>
                      <RadioGroup value={valorSel} onChange={(e) => setValorSel(e.target.value)}>
                        {sel.Valores.map((v) => (
                          <FormControlLabel key={v.Id} value={String(v.Id)} control={<Radio />}
                            label={`${v.Nome} — ${moeda(v.Valor)}`} />
                        ))}
                      </RadioGroup>
                    </>
                  )}

                  {/* 2) Adicional por filho/aluno */}
                  {sel.CobrarPorFilho && (
                    <>
                      {sel.Pago && <Divider sx={{ my: 1.5 }} />}
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Selecione os alunos (opcional):</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Cada aluno com {sel.IdadeMinimaCobranca}+ anos soma {moeda(sel.ValorFilho)} ao valor da inscrição. Abaixo dessa idade: sem adicional.
                        Sem nenhum aluno marcado, a inscrição é registrada somente em seu nome.
                      </Typography>
                      {matriculas === null ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}><CircularProgress size={18} /> Buscando alunos…</Box>
                      ) : Array.isArray(matriculas) && matriculas.length > 0 ? (
                        <Box sx={{ mt: 1 }}>
                          {matriculas.map((m) => {
                            const cobra = m.Idade != null && m.Idade >= sel.IdadeMinimaCobranca;
                            return (
                              <FormControlLabel key={m.Matricula} sx={{ display: "flex" }}
                                control={<Checkbox checked={matSels.includes(m.Matricula)} onChange={() => toggleMat(m.Matricula)} />}
                                label={`${m.NomeAluno || "Aluno"}${m.Turma ? ` — Turma ${m.Turma}` : ""}${m.Idade != null ? ` — ${m.Idade} anos` : ""} • ${cobra ? `+ ${moeda(sel.ValorFilho)}` : "sem adicional"}`} />
                            );
                          })}
                        </Box>
                      ) : matriculas === false ? (
                        <Alert severity="info" sx={{ mt: 1.5 }}>A busca de alunos não está disponível — sua inscrição será registrada em seu nome.</Alert>
                      ) : (
                        <Alert severity="info" sx={{ mt: 1.5 }}>Nenhum aluno vinculado ao seu CPF foi encontrado — sua inscrição será registrada em seu nome.</Alert>
                      )}
                    </>
                  )}

                  {/* Oficinas / opções de escolha */}
                  {gruposComOpcoes().map((g) => (
                    <Box key={g.Id} sx={{ mt: 1.5 }}>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{g.Nome}{g.Obrigatorio ? "" : " (opcional)"}</Typography>
                      <RadioGroup value={opcSel[g.Id] ? String(opcSel[g.Id]) : ""} onChange={(e) => setOpcSel((s) => ({ ...s, [g.Id]: Number(e.target.value) }))}>
                        {g.Opcoes.map((o) => (
                          <FormControlLabel key={o.Id} value={String(o.Id)} disabled={o.Esgotada} control={<Radio />}
                            label={<span>{o.Nome}{o.Vagas != null && (o.Esgotada
                              ? <b style={{ color: "#c62828" }}> — esgotada</b>
                              : <span style={{ color: "#607d8b" }}> — {o.VagasRestantes} vaga(s)</span>)}</span>} />
                        ))}
                      </RadioGroup>
                    </Box>
                  ))}

                  {/* Total */}
                  {(sel.Pago || sel.CobrarPorFilho) ? (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {sel.Pago && <>Inscrição: {moeda(valorBase())}<br /></>}
                      {sel.CobrarPorFilho && <>Adicional dos alunos: {moeda(adicionalFilhos())}<br /></>}
                      <b>Total: {moeda(totalPrevisto())}</b>
                    </Alert>
                  ) : (
                    <Alert severity="info">Evento gratuito. Clique em confirmar para concluir sua inscrição.</Alert>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSel(null)}>Fechar</Button>
          {logado && !resultado && sel?.Aberto && (
            <Button variant="contained" onClick={confirmar} disabled={matriculas === null || enviando}>
              {enviando ? "Enviando…" : "Confirmar inscrição"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog minhas inscrições */}
      <Dialog open={minhasOpen} onClose={() => setMinhasOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Minhas inscrições</DialogTitle>
        <DialogContent dividers>
          {minhas.length === 0 && historico !== null && historico.length === 0 && (
            <Typography variant="body2" color="text.secondary">Você ainda não tem inscrições em eventos.</Typography>
          )}
          {minhas.map((m) => (
            <Box key={m.Id} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{m.EventoNome}</Typography>
                {m.Status === "X" ? <Chip size="small" label="Cancelada" />
                  : m.Pago ? <Chip size="small" color="success" label="Pago" />
                  : m.Status === "C" ? <Chip size="small" color="success" label="Confirmada" />
                  : <Chip size="small" color="warning" label="Aguardando pagamento" />}
                {!m.Pago && m.Status === "A" && m.ValorTotal > 0 && (
                  <Tooltip title="Atualizar status"><IconButton size="small" onClick={() => atualizarMinha(m.Id)}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                )}
              </Stack>
              <ItensCobranca itens={m.Itens} onCertificado={pegarCertificado} />
              {m.ValorTotal > 0 && <Typography variant="caption" color="text.secondary">Total: {moeda(m.ValorTotal)}</Typography>}
              <OficinasCob oficinas={m.Oficinas} />
              <FrequenciaCob encontros={m.Encontros} />
              {!m.Pago && m.Status === "A" && m.ValorTotal > 0 && (
                <>
                  <PixBox cob={m} />
                  <Button size="small" sx={{ mt: 1 }} variant={m.PixCopiaCola ? "text" : "contained"}
                    startIcon={<RefreshIcon />} onClick={() => gerarPixNovamente(m.Id)}>
                    {m.PixCopiaCola ? "Gerar novo PIX" : m.PixExpirado ? "Gerar novo PIX" : "Gerar PIX novamente"}
                  </Button>
                </>
              )}
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))}

          {/* Participações em eventos anteriores (sistema antigo, mesmo CPF) */}
          {historico === null ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Buscando eventos anteriores…</Typography>
            </Box>
          ) : historico.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Eventos anteriores</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Participações registradas no seu CPF antes deste portal — o certificado sai com o número de registro original.
              </Typography>
              {historico.map((h) => (
                <Box key={h.CodInscrito} sx={{ mb: 1.2 }}>
                  <Typography variant="body2">{h.Evento}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {h.DataInicio ? fmtData(h.DataInicio) : "—"}
                    {h.DataFim && fmtData(h.DataFim) !== fmtData(h.DataInicio) ? ` a ${fmtData(h.DataFim)}` : ""}
                    {h.CargaHoraria ? ` • ${h.CargaHoraria}h` : ""}
                    {h.Numero ? ` • certificado nº ${h.Numero}` : ""}
                  </Typography>
                  {h.Liberado ? (
                    <Button size="small" startIcon={<WorkspacePremiumIcon />} onClick={() => pegarCertificadoAntigo(h.CodInscrito)}>
                      Baixar certificado
                    </Button>
                  ) : (
                    <Typography variant="caption" color="text.disabled" display="block">{h.Motivo}</Typography>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setMinhasOpen(false)}>Fechar</Button></DialogActions>
      </Dialog>

      {/* Dialog login avulso (botão Entrar do topo) */}
      <Dialog open={authOpen && !sel} onClose={() => setAuthOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Acessar</DialogTitle>
        <DialogContent dividers><AuthTabs onOk={aposLogin} embutido /></DialogContent>
        <DialogActions><Button onClick={() => setAuthOpen(false)}>Fechar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
